import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import { tradeXRadarSignalRService, type ConnectionStatus } from '@/services/tradeXRadarSignalRService';
import { marketDataService } from '@/services/marketDataService';
import type { MarketDataSnapshot } from '@/models/marketData';
import { newestSnapshot } from '@/models/tradeXRadar';

export const marketDataQueryKey = ['market-data'] as const;

export const useMarketData = () => {
  const queryClient = useQueryClient();
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>(
    navigator.onLine ? 'connecting' : 'offline',
  );
  const [updateNotice, setUpdateNotice] = useState<string | null>(null);
  const previousVersion = useRef<string | null>(null);

  const query = useQuery({
    queryKey: marketDataQueryKey,
    queryFn: async ({ signal }) => {
      try {
        return await marketDataService.fetchMarketData({ signal });
      } catch (error) {
        if (signal.aborted) throw error;
        throw new Error('Unable to refresh market data. Please try again.');
      }
    },
    structuralSharing: (current, incoming) => newestSnapshot(
      current as MarketDataSnapshot | undefined, incoming as MarketDataSnapshot,
    ),
    placeholderData: keepPreviousData,
    staleTime: 15_000,
    retry: 3,
    retryDelay: (attempt) => Math.min(1_000 * 2 ** attempt, 30_000),
    refetchInterval: connectionStatus === 'live' ? false : 45_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  const refreshFromApi = useCallback(() => {
    void queryClient.refetchQueries(
      { queryKey: marketDataQueryKey, type: 'active' },
      { cancelRefetch: false },
    );
  }, [queryClient]);

  useEffect(
    () =>
      tradeXRadarSignalRService.subscribeToTradeXRadarUpdates({
        onStatus: setConnectionStatus,
        onConnected: refreshFromApi,
        onUpdate: refreshFromApi,
      }),
    [refreshFromApi],
  );

  useEffect(() => {
    if (query.isFetching && query.data) {
      setUpdateNotice('Updating market intelligence…');
      return;
    }
    const currentVersion = query.data?.version ?? null;
    if (currentVersion && previousVersion.current && currentVersion !== previousVersion.current) {
      setUpdateNotice('Market intelligence updated');
      const timer = window.setTimeout(() => setUpdateNotice(null), 3_000);
      previousVersion.current = currentVersion;
      return () => window.clearTimeout(timer);
    }
    previousVersion.current = currentVersion;
    if (!query.isFetching) setUpdateNotice(null);
  }, [query.data, query.isFetching]);

  return { ...query, connectionStatus, updateNotice,
    lastUpdated: query.data?.lastUpdated, refresh: query.refetch };
};
